/**
 * User Menu Dropdown (SCR-000)
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-000 Application Shell - User menu
 */

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@myvault/shared/hooks';
import { Icon } from '@myvault/shared/components';

export function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    if (open) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [open]);

  const initials = user?.displayName
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-sm font-medium text-blue-900 hover:bg-amber-400"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Gebruikersmenu"
      >
        {initials}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-lg border border-gray-200 bg-white py-2 shadow-lg">
          {/* User info */}
          <div className="border-b border-gray-100 px-4 pb-3">
            <p className="font-medium text-gray-900">{user?.displayName || 'Gebruiker'}</p>
            <p className="text-sm text-gray-500">{user?.email}</p>
          </div>

          {/* Menu items */}
          <div className="py-1">
            <MenuLink href="/settings/profile" icon="user" label="Mijn profiel" onClick={() => setOpen(false)} />
            <MenuLink href="/settings" icon="cog" label="Instellingen" onClick={() => setOpen(false)} />
            <MenuLink href="/subscription" icon="creditCard" label="Abonnement" onClick={() => setOpen(false)} />
          </div>

          <div className="border-t border-gray-100 py-1">
            {/* Theme toggle placeholder */}
            <div className="flex items-center justify-between px-4 py-2">
              <span className="flex items-center gap-3 text-sm text-gray-700">
                <Icon name="eye" variant="outline" size="sm" className="text-gray-400" />
                Donkere modus
              </span>
              <button
                className="relative h-5 w-9 rounded-full bg-gray-200 transition-colors"
                aria-label="Schakel donkere modus"
              >
                <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform" />
              </button>
            </div>
          </div>

          <div className="border-t border-gray-100 py-1">
            <button
              onClick={() => {
                setOpen(false);
                logout();
              }}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-gray-50"
            >
              <Icon name="arrowRight" variant="outline" size="sm" />
              Uitloggen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuLink({
  href,
  icon,
  label,
  onClick,
}: {
  href: string;
  icon: 'user' | 'cog' | 'creditCard';
  label: string;
  onClick: () => void;
}) {
  const iconMap = {
    user: 'user',
    cog: 'cog',
    creditCard: 'creditCard',
  } as const;

  return (
    <Link
      to={href}
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
    >
      <Icon name={iconMap[icon]} variant="outline" size="sm" className="text-gray-400" />
      {label}
    </Link>
  );
}
